import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import React from 'react';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import {
  useRoadmapExecutiveSummaryScenarioSelection,
} from 'Views/Page/Content/Templates/RoadmapExecutiveSummaryTemplate/useRoadmapExecutiveSummaryScenarioSelection';
import fr from 'facet-rest';
import { displayErrorMessageWithChecks } from 'Context/SnackbarContext/utils';
import { FDStack } from '@facet-wealth/design';
import StepNavigator from 'Components/ComponentLibrary/Molecules/StepNavigator';
import SelectionConfirmationDialog
  from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedDecisionStepTemplate/Content/SelectionConfirmationDialog';
import PropTypes from 'prop-types';
import RoadmapExecutiveSummaryContainer
  from 'Views/Page/Content/Templates/RoadmapExecutiveSummaryTemplate/RoadmapExecutiveSummaryContainer';
import useSelectScenarioContext
  from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedWrapperTemplate/SelectScenarioContext';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import useMobileLayout from 'Hooks/useMobileLayout';

export default function RoadmapExecutiveSummaryStepLayout({ resource }) {
  const { loadPageContentLink } = usePageContentContext();
  const [loadingPrevious, setLoadingPrevious] = React.useState(false);
  const { displayErrorMessage } = useSnackbarContext();
  const isMobile = useMobileLayout();
  const theme = useTheme();

  const {
    onSelectScenarioClick, selectScenario, confirmationDialogResource, setConfirmationDialogResource,
  } = useRoadmapExecutiveSummaryScenarioSelection({ resource });
  const { isSelecting } = useSelectScenarioContext();

  const handlePrevious = async () => {
    setLoadingPrevious(true);
    try {
      await resource.withRelationship(fr.relationships.previousLink.name, async (rel) => {
        await loadPageContentLink(rel.link('related'));
      }, () => null);
    } catch (e) {
      displayErrorMessageWithChecks(e, displayErrorMessage, 'Failed to go back');
    } finally {
      setLoadingPrevious(false);
    }
  };

  const handleNext = resource.withRelationship(
    fr.relationships.selectScenario.name,
    (rel) => async () => {
      await onSelectScenarioClick(rel);
    },
    () => async () => {
      // If there is no select scenario relationship, we assume the next step is to submit the resource.
      const [nextStep] = await resource.submit();
      await loadPageContentLink(nextStep.link('self'));
    },
  );

  return resource.withRelationship(
    fr.relationships.roadmapExecutiveSummary.name,
    (roadmapExecutiveSummaryRel) => (
      <Box sx={{
        height: isMobile ? `calc(100dvh - ${theme.heights.mobileBottomNavigationHeight}px)`
          : `calc(100dvh - ${theme.heights.desktopTopNavigationHeight}px)`,
        overflow: 'auto',
      }}
      >
        <FDStack direction={'column'} minHeight={'100dvh'}>
          <RoadmapExecutiveSummaryContainer relationship={roadmapExecutiveSummaryRel} />
          <Box sx={{ position: 'sticky', bottom: 0 }}>
            <StepNavigator
              onNext={handleNext}
              onPrevious={handlePrevious}
              isLoading={loadingPrevious || isSelecting}
            >
              {resource.attr('primaryActionLabel') || 'Continue'}
            </StepNavigator>
          </Box>
          <SelectionConfirmationDialog
            onSelectScenario={selectScenario}
            confirmationDialogResource={confirmationDialogResource}
            setConfirmationDialogResource={setConfirmationDialogResource}
          />
        </FDStack>
      </Box>
    ),
    () => null,
  );
}

RoadmapExecutiveSummaryStepLayout.defaultProps = {
};

RoadmapExecutiveSummaryStepLayout.propTypes = {
  resource: PropTypes.object.isRequired,
};
